import { BedDouble, Bath, UtensilsCrossed, Ruler, Sailboat, Zap } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal, Overline } from "@/components/motion";
import { IMAGES } from "@/data/site";

const ROUTES = [
  {
    days: "2 zile",
    title: "Vama Veche & 2 Mai",
    desc: "Coasta românească în cel mai boem colț al ei. Baie în golfulețe, apus la cârmă și nopți la ancoră sub cerul liber.",
  },
  {
    days: "3 zile",
    title: "Expediția Kaliakra",
    desc: "Sudul sălbatic: stânci roșii, delfini și ape turcoaz. Ruta perfectă pentru prima croazieră de mai multe zile.",
  },
  {
    days: "4–5 zile",
    title: "Coasta Bulgariei",
    desc: "Balcik, Kavarna, Nesebar — orașe antice, taverne pe faleză și porturi pescărești. Marea călătorie a sezonului.",
  },
];

const BOAT = [
  { icon: Ruler, title: "12,98 m", text: "Lungime totală — suficient de mare pentru confort, suficient de agilă pentru golfulețe." },
  { icon: BedDouble, title: "Cabine duble", text: "Dormitoare generoase cu saltele reale, nu bănci. Dormitul pe mare e cel mai profund somn." },
  { icon: Bath, title: "2 grupuri sanitare", text: "Duș la bord și tot ce ai nevoie pentru mai multe zile departe de ponton." },
  { icon: UtensilsCrossed, title: "Bucătărie completă", text: "Aragaz, frigider și chiuvetă — prânzurile la ancoră au alt gust." },
  { icon: Sailboat, title: "Pânze complete", text: "Randă mare și genoa — navigație adevărată, cu motorul oprit cât se poate de des." },
  { icon: Zap, title: "Autonomie", text: "Rezerve de apă și energie pentru nopți la ancoră, departe de orice port." },
];

const Croaziere = () => (
  <div data-testid="croaziere-page">
    <PageHero
      testid="croaziere-hero"
      image={IMAGES.cruises}
      alt="Velier în navigație dinamică pe mare deschisă"
      overline="Croaziere · 2–5 Zile"
      titleLines={["Marea Neagră,", "mila cu mila."]}
      subtitle="Croaziere de mai multe zile pentru cei care vor mai mult decât o excursie: nopți la ancoră, porturi noi în fiecare dimineață și ritmul real al vieții pe mare."
    />

    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <Reveal className="mb-16">
        <Overline className="mb-6">Rutele Noastre</Overline>
        <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
          Trei drumuri, <span className="italic text-gold">o mie de povești.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {ROUTES.map((route, i) => (
          <Reveal key={route.title} delay={i * 0.12}>
            <div className="flex h-full flex-col border border-white/10 p-8 transition-colors duration-500 hover:border-gold/40 lg:p-10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{route.days}</p>
              <h3 className="mt-5 font-serif text-2xl text-ivory">{route.title}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-mist">{route.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-12 border border-gold/30 p-8 text-center font-serif text-2xl font-light text-ivory lg:p-10">
          De la <span className="text-gold">100 € / persoană / zi</span>
          <span className="text-mist"> · grup privat · rute personalizate la cerere</span>
        </p>
      </Reveal>
    </section>

    <section className="border-y border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16">
          <Overline className="mb-6">Velierul</Overline>
          <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl">
            Sun Odyssey 42.2 — <span className="italic text-gold">casa ta pe apă.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mb-16 overflow-hidden border border-white/10">
            <img
              src="/hope/hope-3.webp"
              alt="Velierul Hope sub pânze pe Marea Neagră, fotografiat de la cârmă"
              data-testid="croaziere-boat-photo"
              className="h-[300px] w-full object-cover md:h-[480px]"
            />
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {BOAT.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06} className="bg-deep">
              <div className="h-full p-8">
                <item.icon className="h-5 w-5 text-gold" />
                <h3 className="mt-5 font-serif text-lg text-ivory">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBanner
      title="Alege ruta. Noi punem pânzele."
      subtitle="Croazierele de mai multe zile se planifică împreună, în funcție de vreme și de ritmul vostru. Hai să vorbim."
    />
  </div>
);

export default Croaziere;
